// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { AddEditPatientType } from "../types";
import { updatePatient } from "../api";

export const useUpdatePatient = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (inputData: {
      patientId: string;
      patientData: AddEditPatientType;
    }) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return updatePatient(
          token,
          clinicId,
          inputData.patientId,
          inputData.patientData
        );
      } catch (error) {
        throw error;
      }
    },
    // Invalidate patients query cache
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      setSnackbarInfo({
        variant: "success",
        message: t("Patient updated successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Patient could not be updated!"),
      });
    },
  });
};
